
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react'
import { MapToolsStore } from '@openlayers/MapToolsStore';
import { CalcSurface } from '../../../../../../typings/reality-capture/volumes/models';
import { ButtonSelectSurface } from './surface-selection';
import { Button } from '@shared/ui-react/Button';
import { mapRootStore, mapUI } from '@pages/map/map';
import { ActiveAlignmentDropdown } from '@openlayers/alignments/ui/ActiveAlignmentDropdown';
import { generateAlignmentArea } from '../../utils/helpers/helpers';
import type { CorridorSide } from '../../utils/helpers/helpers';

export const AlignmentSelect = observer(({
    toolsStore,
    reference,
    target,
    selectReference,
    selectTarget,
    clearReference,
    clearTarget,
}: {
    toolsStore: MapToolsStore;
    reference: CalcSurface | null;
    target: CalcSurface | null;
    selectReference: (reference: CalcSurface) => void;
    selectTarget: (target: CalcSurface) => void;
    clearReference: () => void;
    clearTarget: () => void;
}) => {
    const [ alignmentModelId, setAlignmentModelId ] = useState<number>(-1);
    const [ alignmentIndex, setAlignmentIndex ] = useState<number>(0);
    const [ startStation, setStartStation ] = useState<number | ''>('');
    const [ endStation, setEndStation ] = useState<number | ''>('');
    const [ corridorWidth, setCorridorWidth ] = useState<number | ''>(20);
    const [ side, setSide ] = useState<CorridorSide>('both');

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (alignmentModelId > 0 && startStation !== '' && endStation !== '' && corridorWidth !== '') {
                generateAlignmentArea(toolsStore, alignmentModelId, alignmentIndex, startStation as number, endStation as number, corridorWidth as number, side);
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [alignmentModelId, alignmentIndex, startStation, endStation, corridorWidth, side, toolsStore]);

    const sideOptions: { value: CorridorSide; label: string }[] = [
        { value: 'both', label: __('js_map_measuring_tools.side_both') },
        { value: 'left', label: __('js_map_measuring_tools.side_left') },
        { value: 'right', label: __('js_map_measuring_tools.side_right') },
    ];

    return (
        <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-800 tracking-tight text-sm">1. {__('js_map_measuring_tools.alignment')}</span>
            </div>
            <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 min-w-0">
                    <ActiveAlignmentDropdown
                        filetreeData={mapRootStore.treeData}
                        selectedModelId={alignmentModelId}
                        selectedIndex={alignmentIndex}
                        onChange={(id, index) => {
                            setAlignmentModelId(id);
                            setAlignmentIndex(index);
                        }}
                        storageKey='Map2d_volumeAlignmentSelect'
                        alignmentStore={mapRootStore.alignmentStore}
                    />
                </div>
                <Button
                    secondary
                    small
                    icon='iki iki-fit-to-screen'
                    tooltip={__('js_map.fit_into_view')}
                    onClick={() => mapUI?.fitActiveAlignment()}
                    disabled={alignmentModelId < 0}
                    style={{ borderRadius: 'var(--ik-shape-br-xl)' }}
                />
            </div>

            <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-800 tracking-tight text-sm">2. {__('js_map_measuring_tools.stations_and_corridor')}</span>
            </div>
            <div className="flex flex-col gap-3 mb-4 bg-ik-surface-dim p-3 rounded border border-ik-border-subtle">
                <div className="flex items-center gap-2">
                    <span className="w-24 text-sm text-ik-text-weak">{__('js_map_measuring_tools.start_station')} (m):</span>
                    <input
                        type="number"
                        value={startStation}
                        onChange={e => setStartStation(e.target.value !== '' ? parseFloat(e.target.value) : '')}
                        className="border border-ik-border-default rounded px-2 py-1 flex-1 text-sm"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-24 text-sm text-ik-text-weak">{__('js_map_measuring_tools.end_station')} (m):</span>
                    <input
                        type="number"
                        value={endStation}
                        onChange={e => setEndStation(e.target.value !== '' ? parseFloat(e.target.value) : '')}
                        className="border border-ik-border-default rounded px-2 py-1 flex-1 text-sm"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-24 text-sm text-ik-text-weak">{__('js_map_measuring_tools.corridor_width')} (m):</span>
                    <input
                        type="number"
                        value={corridorWidth}
                        onChange={e => setCorridorWidth(e.target.value !== '' ? parseFloat(e.target.value) : '')}
                        className="border border-ik-border-default rounded px-2 py-1 flex-1 text-sm"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-24 text-sm text-ik-text-weak">{__('js_map_measuring_tools.side')}:</span>
                    <div className="flex bg-ik-surface-dim rounded-md p-1 border border-ik-border-weak w-fit">
                        {sideOptions.map(option => (
                            <div
                                key={option.value}
                                role="button"
                                aria-pressed={side === option.value}
                                className={`px-3 py-1 rounded text-xs font-medium transition-all cursor-pointer select-none ${side === option.value ? 'bg-ik-accent-primary text-white shadow-sm' : 'text-ik-text-default hover:bg-ik-surface-hover'}`}
                                onClick={() => setSide(option.value)}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-800 tracking-tight text-sm">3. {__('js_map_measuring_tools.from_surface')}</span>
            </div>
            <div className="mb-4">
                <ButtonSelectSurface
                    toolsStore={toolsStore}
                    selection={reference}
                    onSelect={selectReference}
                    onClear={clearReference}
                />
            </div>
            <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-800 tracking-tight text-sm">4. {__('js_map_measuring_tools.to_surface')}</span>
            </div>
            <div className="mb-2">
                <ButtonSelectSurface
                    toolsStore={toolsStore}
                    selection={target}
                    onSelect={selectTarget}
                    onClear={clearTarget}
                />
            </div>
        </div>
    );
});